import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { ROUTE_NAMES } from '@tsgecart/config';
import { useAppUser } from './use-app-user.js';

/**
 * These guards are a UX convenience only -- they hide/redirect around
 * content so a signed-out or under-privileged visitor doesn't see a flash
 * of a page they can't actually use. They grant no real access and must
 * never be treated as the security boundary: every protected Worker route
 * independently re-verifies the Clerk token and re-resolves roles/
 * permissions from Neon on every single request (see
 * requireClerkAuth/requireRole in apps/worker), so a user who bypassed
 * these guards client-side (e.g. via devtools) still gets a real 401/403
 * from the API. See docs/security-model.md "Frontend guards are UX only".
 */

export function FullPageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div
        className="border-brand h-8 w-8 animate-spin rounded-full border-4 border-t-transparent"
        role="status"
        aria-label="Loading"
      />
    </div>
  );
}

/** Redirects a signed-out visitor to /sign-in, preserving the path they tried to reach as a validated (see safe-redirect.ts) return path. Waits for Clerk to finish loading before deciding, so an authenticated visitor never sees a sign-in flash on refresh. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();

  if (!isLoaded) return <FullPageLoader />;

  if (!isSignedIn) {
    const returnTo = `${location.pathname}${location.search}`;
    return (
      <Navigate to={`${ROUTE_NAMES.signIn}?redirect=${encodeURIComponent(returnTo)}`} replace />
    );
  }

  return <>{children}</>;
}

/**
 * Role-gates content already inside a RequireAuth boundary (compose them:
 * `<RequireAuth><RequireRole role="ADMIN">...`). Role membership comes
 * exclusively from GET /api/v1/auth/me's backend-resolved `roles` array --
 * never from Clerk session/publicMetadata claims, which this app's Worker
 * never trusts as authorization in the first place.
 */
export function RequireRole({ role, children }: { role: string; children: ReactNode }) {
  const { data, isLoading, isError } = useAppUser();

  if (isLoading) return <FullPageLoader />;
  // Covers both cases the backend deliberately never distinguishes in its
  // response (see docs/security-model.md): the role genuinely isn't
  // present, or the account itself isn't `active` (locked/suspended/
  // disabled/deleted -- GET /auth/me returns AUTHORIZATION_ERROR for all
  // of these, which surfaces here as isError). Either way, the same
  // generic forbidden page is shown, never home (which would silently
  // imply "you're just not signed in the right way").
  if (isError || !data?.roles.includes(role)) return <Navigate to={ROUTE_NAMES.forbidden} replace />;

  return <>{children}</>;
}

export function RequireAnyRole({ roles, children }: { roles: string[]; children: ReactNode }) {
  const { data, isLoading, isError } = useAppUser();

  if (isLoading) return <FullPageLoader />;
  if (isError || !data?.roles.some((role) => roles.includes(role))) {
    return <Navigate to={ROUTE_NAMES.forbidden} replace />;
  }

  return <>{children}</>;
}

/** Admin dashboard access is ADMIN or STAFF -- matches the Worker's `requireAnyRole(['ADMIN', 'STAFF'])` gate on every admin route (see rbac.md), not ADMIN-only. */
export function RequireAdmin({ children }: { children: ReactNode }) {
  return <RequireAnyRole roles={['ADMIN', 'STAFF']}>{children}</RequireAnyRole>;
}
